import { Link, useDisclosure, Modal, ModalContent, ModalHeader, ModalBody, Tabs, Tab, ModalFooter } from '@nextui-org/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExplainerDetails from './explainerdetails';
import ExplainerDocuments from './explainerDocuments';
import ExplainerImages from './explainerImages';
import Explainerranking from './explainerranking';
import ExplainerAnswer from './explaineranswer';
import JudgeAnalysisComponent from './judgeanalysis';
import { RootState, getMessageByIndex, useGetExplainAnswerDetailsMutation } from '@circuitry-ai/doc-data';
import { useSelector } from 'react-redux';
import { checkType, CtyButton, CtyShowToast, CtySpinner } from '@circuitry-ai/doc-ui';

enum TabKeys {
    QUESTION = 'Question',
    KNOWLEDGE = 'Knowledge',
    RANKING = 'Ranking',
    IMAGES = 'Images',
    ANSWER = 'Answer',
    JUDGE_ANALYSIS = 'Evaluation Analysis',
}

//TODO: update Props type

const ExplainerModal = ({ setViewExplainer, explainerAnswer, msgIndex, userQuery, metadataMap = {}, advisorResponse = '' }: any) => {
    const { t } = useTranslation();
    const { isOpen, onOpen } = useDisclosure();
    useEffect(() => { onOpen(); }, []);
    const currentQuestion = useSelector((state: RootState) => getMessageByIndex(state, msgIndex));
    const [currentTab, setCurrentTab] = useState<TabKeys>(TabKeys.QUESTION);
    const currentQuestionAnswerTime = metadataMap?.total_time_taken_seconds ?? 0;

    const questionDetails = {
        query: currentQuestion ?? userQuery,
        metadata: explainerAnswer?.question?.metadata?.[0],
        total_knowledge: explainerAnswer?.question?.total_knowledge,
        top_results: explainerAnswer?.question?.top_results,
        rephrased_query: explainerAnswer?.question?.rephrased_query,
        reranker_latency: explainerAnswer?.question?.time_taken?.reranker_latency,
        retriever_latency: explainerAnswer?.question?.time_taken?.retriever_latency,
        secondary_intent_latency: explainerAnswer?.question?.time_taken?.secondary_intent_latency,
        time_taken_retriever: explainerAnswer?.metrics?.retriever?.time.toFixed(2),
        time_taken_reranker: explainerAnswer?.metrics?.reranker?.time.toFixed(2),
        llm_model_reranker: explainerAnswer?.metrics?.reranker?.llm_model,
        time_taken_image_retriever:
            explainerAnswer?.metrics?.image_retriever?.time != null
                ? Number(explainerAnswer.metrics.image_retriever.time).toFixed(2)
                : undefined,
        llm_model_image_retriever: explainerAnswer?.metrics?.image_retriever?.llm_model
    };

    const validImageEntries = useMemo(
        () =>
            (explainerAnswer?.image_entries ?? []).filter(
                (e: { img_path?: string }) => e?.img_path && String(e.img_path).trim() !== ''
            ),
        [explainerAnswer?.image_entries]
    );
    const showImagesTab = validImageEntries.length > 0;
    const showJudgeAnalysisTab = Boolean(explainerAnswer?.judge_analysis);

    const tabItems = [
        { key: TabKeys.QUESTION, title: `${t(TabKeys.QUESTION)}`, id: 'Details-tab' },
        { key: TabKeys.KNOWLEDGE, title: `${t(TabKeys.KNOWLEDGE)}`, id: 'Documents-tab' },
        { key: TabKeys.RANKING, title: `${t(TabKeys.RANKING)}`, id: 'Ranking-tab' },
        ...(showImagesTab ? [{ key: TabKeys.IMAGES, title: `${t(TabKeys.IMAGES)}`, id: 'Images-tab' }] : []),
        { key: TabKeys.ANSWER, title: `${t(TabKeys.ANSWER)}`, id: 'Answer-tab' },
        ...(showJudgeAnalysisTab ? [{ key: TabKeys.JUDGE_ANALYSIS, title: `${t(TabKeys.JUDGE_ANALYSIS)}`, id: 'Judge-Analysis-tab' }] : []),
    ];

    const renderSelectedTab = () => {
        switch (currentTab) {
            case TabKeys.QUESTION:
                return <ExplainerDetails details={questionDetails} rephraser_confidence_score={metadataMap?.rephraser_confidence_score ?? ''}/>;
            case TabKeys.KNOWLEDGE:
                return <ExplainerDocuments details={questionDetails} documentsList={explainerAnswer?.retrieved_contexts ?? []} advisor_id={metadataMap?.advisor_id ?? ''} />;
            case TabKeys.RANKING:
                return <Explainerranking details={questionDetails} rankingList={explainerAnswer?.reranked_contexts ?? []} advisor_id={metadataMap?.advisor_id ?? ''} />;
            case TabKeys.IMAGES:
                return (
                    <ExplainerImages
                        details={questionDetails}
                        imageEntries={validImageEntries}
                        advisor_id={metadataMap?.advisor_id ?? ''}
                    />
                );
            case TabKeys.ANSWER: {
                // Use API response first, fallback to advisor response if API response is empty or not present
                const apiAnswer = explainerAnswer?.answer;
                const answerToDisplay = apiAnswer && apiAnswer.trim() !== '' ? apiAnswer : advisorResponse;
                // Try metadataMap first, then fallback to currentQuestion metaData
                const sourceIds = metadataMap?.source_ids ?? currentQuestion?.metaData?.[1]?.source_ids ?? [];
                const explainerAnswerDetails = {
                    answer: answerToDisplay ?? '',
                    timeTaken: currentQuestionAnswerTime,
                    messageId: metadataMap?.message_id ?? '',
                    query: currentQuestion ?? userQuery,
                    sourceIds: sourceIds,
                    advisorId: metadataMap?.advisor_id ?? '',
                    time_taken_answer: explainerAnswer?.metrics?.answer_generation?.time.toFixed(2),
                    llm_model_answer: explainerAnswer?.metrics?.answer_generation?.llm_model
                }
                return <ExplainerAnswer {...explainerAnswerDetails} />;
            }
            case TabKeys.JUDGE_ANALYSIS: {
                const advisorAnswer = explainerAnswer?.answer?.trim() ? explainerAnswer.answer : advisorResponse;
                const expertAnswer = typeof explainerAnswer?.expert_answer === 'string' ? explainerAnswer.expert_answer : '';
                return <JudgeAnalysisComponent
                    judgeAnalysis={explainerAnswer?.judge_analysis}
                    mailContent={typeof explainerAnswer?.mail_content === 'string' ? explainerAnswer.mail_content : undefined}
                    advisorAnswer={advisorAnswer}
                    expertAnswer={expertAnswer}
                />;
            }
        }
    };

    const hasExplainerData = (explainerAnswer?.retrieved_contexts?.length ?? 0) > 0 || showJudgeAnalysisTab;

    return (
        <Modal id="translate-modal" backdrop={"blur"} isOpen={isOpen} onClose={() => setViewExplainer(false)} className="max-h-[80%] max-w-[80%]" isDismissable={false} isKeyboardDismissDisabled={true}>
            <ModalContent>
                <>
                    <ModalHeader className='flex flex-col'>
                        <h1 className="text-xl font-medium">Advisor explainer</h1>
                        <div className="text-base font-normal pt-2">View how this answer was generated based on the metadata, knowledge, ranking.</div>
                    </ModalHeader>
                    {hasExplainerData ?
                        <ModalBody className="overflow-y-auto">
                            <Tabs radius={'sm'} aria-label="Tabs radius" fullWidth size='lg' items={tabItems} selectedKey={currentTab} onSelectionChange={(key) => setCurrentTab(key as TabKeys)}>
                                {(item) => <Tab key={item.key} title={item.title} id={`${item.title}-tab`} />}
                            </Tabs>
                            {renderSelectedTab()}
                        </ModalBody> : <ModalBody className="overflow-y-auto">
                            <div className="text-center text-lg font-medium">Explainer is not available for this answer</div>
                        </ModalBody>}
                    <ModalFooter>
                        <CtyButton id={`explainer-modal-close-btn`} onPress={() => setViewExplainer(false)} variant='bordered' size="lg">Close</CtyButton>
                    </ModalFooter>
                </>
            </ModalContent>
        </Modal>
    );
};


const ExplainAdvisorAnswer = ({ msgIndex, metadataMap, userQuery, advisorResponse = '' }: { msgIndex: number, metadataMap: Record<string, any>, userQuery: string, advisorResponse?: string }) => {
    const [viewExplainer, setViewExplainer] = useState<boolean>(false);
    const [getExplainAnswerDetails, { isLoading: explainerAnswerIsLoading }] = useGetExplainAnswerDetailsMutation();
    const [explainerAnswer, setExplainerAnswer] = useState<any>('');

    const fetchExplainerAnswer = async () => {
        const messageId = metadataMap?.message_id ?? '';
        try {
            const expertAnswerResponse = await getExplainAnswerDetails(messageId).unwrap();
            // API may return either explainer payload directly or wrapped under `data`.
            setExplainerAnswer(expertAnswerResponse?.data ?? expertAnswerResponse);
            setViewExplainer(true);
        } catch (e) {
            CtyShowToast(checkType.error, (e as Error)?.message || 'Error Saving Answer');
            setViewExplainer(false);
        }
    };

    const explainerModalProps = { setViewExplainer, explainerAnswer, msgIndex, userQuery, metadataMap, advisorResponse };

    return (
        <>
            <Link id={`explain-this-answer-link`} onPress={fetchExplainerAnswer} size="md" className='text-[#0BA5EC] font-medium cursor-pointer mt-3'>
                <span className={`flex ${explainerAnswerIsLoading && "gap-1"}`}>
                    <span>{explainerAnswerIsLoading && (<CtySpinner size='sm' />)}</span>[Explain this answer]
                </span>
            </Link>
            {viewExplainer && <ExplainerModal {...explainerModalProps} />}
        </>
    );
};

export default ExplainAdvisorAnswer;